import type { PreviewConnectionStatus, PreviewDebugInfo, PreviewLayer } from '../../stores/preview';
import { createSandboxIframeResetState, usePreviewStore } from '../../stores/preview';
import { previewStartupTracker } from './previewStartupTrackingRuntime';

let iframeLoadIdSequence = 0;
let previewTraceEnabled = false;

function createIframeLoadId() {
  iframeLoadIdSequence += 1;
  return `${Date.now()}-${iframeLoadIdSequence}`;
}

export function getPreviewNow() {
  return typeof performance !== 'undefined' ? performance.now() : Date.now();
}

export function createIframeLoadCycle(routeOverride = usePreviewStore.getState().previewRouteOverride) {
  const state = usePreviewStore.getState();
  return {
    iframeLoadId: createIframeLoadId(),
    iframeLoadStartedAt: getPreviewNow(),
    pendingPreviewNavigation: null,
    sandboxDocumentRouteOverride: routeOverride,
    deliveryPinnedPreviewUrl: state.deliveryExpectedVersionId ? state.deliveryPinnedPreviewUrl : undefined,
  };
}

function createPreviewDebugInfo(): PreviewDebugInfo {
  return {
    bridgeStatus: 'idle',
    pluginLoadStatus: null,
  };
}

export function attachPreviewStartupIframeLoad(
  previousIframeLoadId: string,
  source: 'sandbox_refresh' | 'iframe_load',
) {
  const currentState = usePreviewStore.getState();
  if (
    currentState.iframeLoadId
    && currentState.iframeLoadId !== previousIframeLoadId
    && currentState.sandboxUrl
  ) {
    previewStartupTracker.attachIframeLoad(
      currentState.iframeLoadId,
      currentState.iframeLoadStartedAt,
      source,
    );
  }
}

export function tracePreview(event: string, details: Record<string, unknown> = {}) {
  if (!previewTraceEnabled) {
    return;
  }

  const state = usePreviewStore.getState();
  // eslint-disable-next-line no-console
  console.info(`[PreviewTrace] ${event}`, {
    iframeLoadId: state.iframeLoadId || '-',
    target: state.targetLayer,
    liveStatus: state.livePreviewStatus,
    runtimeStatus: state.sandboxRuntimeStatus,
    runtimeConnection: state.sandboxRuntimeConnectionStatus,
    contentReady: state.sandboxContentReady,
    iframeReady: state.sandboxIframeReady,
    problem: state.livePreviewProblem ?? '-',
    ...details,
  });
}

export function setPreviewTraceEnabled(enabled: boolean) {
  previewTraceEnabled = enabled;
}

export function setTargetLayer(targetLayer: PreviewLayer) {
  const previewState = usePreviewStore.getState();
  const previousTarget = previewState.targetLayer;
  usePreviewStore.setState(state => {
    if (
      targetLayer === 'live'
      && state.deliveryExpectedVersionId
      && state.activePreviewSource === 'pre-release'
    ) {
      return {};
    }
    return state.targetLayer === targetLayer
      ? {}
      : {
          targetLayer,
          activePreviewSource: targetLayer === 'live' ? 'sandbox' : 'pre-release',
          pendingPreviewNavigation: null,
        };
  });
  const currentTarget = usePreviewStore.getState().targetLayer;
  if (previousTarget !== currentTarget) {
    tracePreview('target.changed', { previousTarget, target: currentTarget });
  }
}

export function setSandboxPreviewUrl(sandboxUrl: string) {
  const previousState = usePreviewStore.getState();
  const previousUrl = previousState.sandboxUrl;
  usePreviewStore.setState(state => {
    if (state.sandboxUrl === sandboxUrl) {
      return {};
    }
    if (state.deliveryExpectedVersionId && state.deliveryPinnedPreviewUrl) {
      return { sandboxUrl };
    }
    return {
      sandboxUrl,
      sandboxRefreshNonce: state.sandboxRefreshNonce + 1,
      ...createIframeLoadCycle(),
      ...createSandboxIframeResetState(),
      livePreviewStatus: 'connecting',
      sandboxRuntimeConnectionStatus: sandboxUrl ? 'connecting' : 'idle',
      livePreviewProblem: undefined,
      iframeCapabilities: { elementInspector: false },
      ...(!sandboxUrl
        ? {
            previewDebugInfo: {
              ...state.previewDebugInfo,
              live: createPreviewDebugInfo(),
            },
          }
        : {}),
    };
  });
  if (previousUrl !== sandboxUrl) {
    tracePreview('iframe.live.cycle-started', { reason: 'url-changed', hasUrl: Boolean(sandboxUrl) });
  }
  attachPreviewStartupIframeLoad(previousState.iframeLoadId, 'iframe_load');
}

export function beginSandboxEnsurePreviewCycle() {
  usePreviewStore.setState({
    ...createIframeLoadCycle(),
    ...createSandboxIframeResetState(),
    livePreviewStatus: 'connecting',
    sandboxRuntimeStatus: 'booting',
    sandboxLiveness: 'booting',
    sandboxRuntimeConnectionStatus: 'connecting',
    livePreviewProblem: 'booting',
    iframeCapabilities: { elementInspector: false },
  });
  tracePreview('iframe.live.cycle-started', { reason: 'sandbox-ensure-wait' });
}

export function reloadSandboxPreview() {
  const previousState = usePreviewStore.getState();
  usePreviewStore.setState(state => {
    if (state.deliveryExpectedVersionId) {
      return state;
    }
    return {
      sandboxRefreshNonce: state.sandboxRefreshNonce + 1,
      ...createIframeLoadCycle(),
      ...createSandboxIframeResetState(),
      livePreviewStatus: state.sandboxUrl ? 'connecting' : state.livePreviewStatus,
      sandboxRuntimeConnectionStatus: state.sandboxUrl ? 'connecting' : state.sandboxRuntimeConnectionStatus,
      livePreviewProblem: undefined,
      refreshingIframeType: null,
      iframeCapabilities: { elementInspector: false },
    };
  });
  attachPreviewStartupIframeLoad(previousState.iframeLoadId, 'sandbox_refresh');
  tracePreview('iframe.live.cycle-started', { reason: 'reload' });
}

export function beginPreviewRefresh() {
  const previousState = usePreviewStore.getState();
  if (!previousState.deliveryExpectedVersionId) {
    previewStartupTracker.start('sandbox_refresh');
  }
  usePreviewStore.setState(state => state.deliveryExpectedVersionId
    ? state
    : {
        targetLayer: 'live',
        activePreviewSource: 'sandbox',
        pageStatus: { hasError: false, errorMessage: '' },
        livePreviewStatus: 'connecting',
        sandboxRuntimeStatus: 'recovering',
        pendingPreviewNavigation: null,
        sandboxRuntimeConnectionStatus: 'connecting',
        ...createSandboxIframeResetState(),
        livePreviewProblem: undefined,
        preReleaseIframeReady: false,
        refreshingIframeType: null,
        iframeCapabilities: { elementInspector: false },
      });
  tracePreview('refresh.state-reset');
}

export function refreshPreviewIframe(iframeType: PreviewLayer) {
  const previousState = usePreviewStore.getState();
  usePreviewStore.setState(state => {
    if (iframeType === 'live') {
      return state.sandboxUrl && !state.deliveryExpectedVersionId
        ? {
            targetLayer: 'live',
            activePreviewSource: 'sandbox',
            pageStatus: { hasError: false, errorMessage: '' },
            refreshingIframeType: 'live',
            sandboxRefreshNonce: state.sandboxRefreshNonce + 1,
            ...createIframeLoadCycle(),
            ...createSandboxIframeResetState(),
            livePreviewStatus: 'connecting',
            sandboxRuntimeStatus: 'recovering',
            sandboxRuntimeConnectionStatus: 'connecting',
            livePreviewProblem: undefined,
            iframeCapabilities: { elementInspector: false },
          }
        : {};
    }

    return state.preReleaseUrl
      ? {
          targetLayer: 'stable',
          activePreviewSource: 'pre-release',
          refreshingIframeType: 'stable',
          previewNavigationKeys: {
            ...state.previewNavigationKeys,
            stable: state.previewNavigationKeys.stable + 1,
          },
          preReleaseIframeReady: false,
        }
      : {};
  });
  const currentState = usePreviewStore.getState();
  if (iframeType === 'live' && currentState.iframeLoadId !== previousState.iframeLoadId) {
    attachPreviewStartupIframeLoad(previousState.iframeLoadId, 'sandbox_refresh');
    tracePreview('iframe.live.cycle-started', { reason: 'refresh' });
  } else if (
    iframeType === 'stable' &&
    currentState.previewNavigationKeys.stable !== previousState.previewNavigationKeys.stable
  ) {
    tracePreview('iframe.stable.cycle-started', { reason: 'refresh' });
  } else {
    tracePreview('iframe.refresh-skipped', { iframeType });
  }
}

export function completePreviewIframeRefresh(iframeType: PreviewLayer) {
  usePreviewStore.setState(state => (state.refreshingIframeType === iframeType ? { refreshingIframeType: null } : {}));
}

export function setStablePreviewUrl(preReleaseUrl: string) {
  const previousUrl = usePreviewStore.getState().preReleaseUrl;
  usePreviewStore.setState(state => state.deliveryExpectedVersionId
    ? state
    : {
        preReleaseUrl,
        ...(!preReleaseUrl
          ? {
              previewDebugInfo: {
                ...state.previewDebugInfo,
                stable: createPreviewDebugInfo(),
              },
            }
          : {}),
      });
  if (previousUrl !== preReleaseUrl) {
    tracePreview('iframe.stable.url-updated', { hasUrl: Boolean(preReleaseUrl) });
  }
}

export function setSandboxIframeReady(sandboxIframeReady: boolean) {
  const previousReady = usePreviewStore.getState().sandboxIframeReady;
  usePreviewStore.getState().setSandboxIframeReady(sandboxIframeReady);
  if (!sandboxIframeReady) {
    usePreviewStore.setState({ iframeCapabilities: { elementInspector: false } });
  }
  if (previousReady !== sandboxIframeReady) {
    tracePreview('iframe.live.ready-changed', { ready: sandboxIframeReady });
  }
}

export function setSandboxContentReady(sandboxContentReady: boolean) {
  const previousReady = usePreviewStore.getState().sandboxContentReady;
  usePreviewStore.setState({ sandboxContentReady });
  usePreviewStore.getState().setSandboxAppContentReady(sandboxContentReady);
  if (previousReady !== sandboxContentReady) {
    tracePreview('content.ready-changed', { ready: sandboxContentReady });
  }
}

export function startPreviewDebugTracking(iframeType: PreviewLayer) {
  usePreviewStore.setState(state => ({
    previewDebugInfo: {
      ...state.previewDebugInfo,
      [iframeType]: {
        bridgeStatus: 'connecting',
        pluginLoadStatus: null,
      },
    },
  }));
}

export function setPreviewDebugBridgeStatus(iframeType: PreviewLayer, bridgeStatus: PreviewDebugInfo['bridgeStatus']) {
  const previousStatus = usePreviewStore.getState().previewDebugInfo[iframeType].bridgeStatus;
  usePreviewStore.setState(state => ({
    previewDebugInfo: {
      ...state.previewDebugInfo,
      [iframeType]: { ...state.previewDebugInfo[iframeType], bridgeStatus },
    },
  }));
  if (previousStatus !== bridgeStatus) {
    tracePreview('bridge.status-changed', { iframeType, status: bridgeStatus });
  }
}

export function setSandboxRuntimeConnectionStatus(runtimeConnectionStatus: PreviewConnectionStatus) {
  const previousStatus = usePreviewStore.getState().sandboxRuntimeConnectionStatus;
  usePreviewStore.setState({ sandboxRuntimeConnectionStatus: runtimeConnectionStatus });
  if (previousStatus !== runtimeConnectionStatus) {
    tracePreview('runtime.status-changed', { status: runtimeConnectionStatus });
  }
}

export function setPreviewDebugPluginStatus(
  iframeType: PreviewLayer,
  pluginLoadStatus: PreviewDebugInfo['pluginLoadStatus'],
) {
  usePreviewStore.setState(state => ({
    previewDebugInfo: {
      ...state.previewDebugInfo,
      [iframeType]: { ...state.previewDebugInfo[iframeType], pluginLoadStatus },
    },
  }));
}
